import { api } from 'api';
import {
  default as IntroductionHHMTIcon,
  default as IntroductionRemoxIcon,
} from 'assets/ver2/icons/introduction-hhmt-grey-fill-icon.svg';
import { getNumberUnmaskStr } from 'components/FormEditReceiver/func';
import {
  DESKTOP_MIN_WIDTH,
  METHOD_BANK,
  METHOD_DEPOSIT,
  METHOD_EPAYMENT,
  METHOD_EWALLET,
  METHOD_HOME,
  METHOD_PAYID,
  METHOD_PAYTO,
  METHOD_PICKUP,
  METHOD_POLI,
  METHOD_TRANSLATE_REFERECES,
  PAYMENT_METHOD_REFERENCE,
  SCREEN_ID_REMITTANCE_DETAILS,
  SENDING_AMOUNT_DISCOUNT_PERCENTS,
  STATUS_CANCELLED,
  STATUS_COMPLETED,
  STATUS_DELIVERING,
  STATUS_ON_HOLD,
  STATUS_PROCESSING,
  STATUS_SUBMITTED,
} from 'constants';
import { format, formatter } from 'helpers';
import { checkMaintenance } from 'helpers/maintenance';
import useAutoShowEventModal from 'hooks/useAutoShowEventModal';
import useCountryConfig from 'hooks/useCountryConfig';
import useGetConfig from 'hooks/useGetConfig';
import useLang from 'hooks/useLang';
import useStore from 'hooks/useStore';
import useTheme from 'hooks/useTheme';
import useThrottle from 'hooks/useThrottle';
import { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { SET_TRANSACTION_DETAILS } from 'store/action';
import styled from 'styled-components';
import { textBreakLineAllMixin } from 'styles';
import { numberUtils, stringUtils } from 'utils';
import ButtonCommon from '../../../common/ButtonCommon';
import CustomCalculatorFormAccordion from '../CustomCalculatorFormAccordion';
import InstructionPopup from '../InstructionPopup';
import TransactionStatus from '../TransactionStatus';

const SCREEN_ID = SCREEN_ID_REMITTANCE_DETAILS;

const FormTransactionDetails = ({
  isOpen,
  information,
  setInformation,
  setTransactions,
  applyFiltered,
  searchValue,
  currentSearchBy,
  currentPage,
  setCurrentPage,
  setTotalElements,
  setTotalPages,
  onClose,
  onClick,
}) => {
  const { t } = useLang();
  const { state, dispatch } = useStore();
  const { throttle } = useThrottle(1);
  const { isHHMTTheme } = useTheme();

  const { transactionDetails } = state;

  const {
    refNumber,
    status,
    fullName,
    fullAddress,
    mobile,
    phone,
    payout,
    receiveAmount,
    currencyCode,
    rateValue,
    countryCode,
    currencyCodeAnother,
    receiveAmountAnother,
    rateValueAnother,
    payment,
    sendingDate,
    sendAmount,
    paid,
    feeAmount,
    surchargeAmount,
    totalPayAmount,
    discountAmount,
    discountApplied,
    dueDateTime,
    paytoAgreement,
    allowMakePayment,
  } = information || transactionDetails || {};

  const isDiscountApplied = Boolean(Number(discountAmount));

  const countryConfig = useCountryConfig(countryCode);
  const config = useGetConfig({ countryConfig, dmCode: payout?.dmCode });
  const {
    mobile: mobileConfig,
    phone: phoneConfig,
    accountNumber: accountNumberConfig,
    ewalletNumber: ewalletNumberConfig,
    accountBsb: accountBsbConfig,
    accountSwift: accountSwiftConfig,
  } = config;
  const accountNumberFormat = formatter.formatParse(
    accountNumberConfig?.format,
  );
  const accountBsbFormat = formatter.formatParse(accountBsbConfig?.format);
  const accountSwiftFormat = formatter.formatParse(accountSwiftConfig?.format);
  const ewalletNumberFormat = formatter.formatParse(
    ewalletNumberConfig?.format,
  );

  const [isOpenInstructionPopup, setOpenInstructionPopup] = useState(false);
  const [originalFee, setOriginalFee] = useState(0);

  const mobileFormat = formatter.formatParse(mobileConfig?.format);
  const mobileNumberMask = formatter.mask(
    getNumberUnmaskStr(mobile, mobileFormat),
    mobileFormat,
  );

  const phoneFormat = formatter.formatParse(phoneConfig?.format);
  const phoneNumberMask = formatter.mask(
    getNumberUnmaskStr(phone, phoneFormat),
    phoneFormat,
  );

  // payout code
  const isPayoutMethodByHome = payout?.dmCode === METHOD_HOME;
  const isPayoutMethodByPickup = payout?.dmCode === METHOD_PICKUP;
  const isPayoutMethodByBank = payout?.dmCode === METHOD_BANK;
  const isPayoutMethodByEWallet = payout?.dmCode === METHOD_EWALLET;

  // payment method
  const isPaymentMethodByDeposit = payment?.paymentMethod === METHOD_DEPOSIT;
  const isPaymentMethodByPayId = payment?.paymentMethod === METHOD_PAYID;
  const isPaymentMethodByEPayment = payment?.paymentMethod === METHOD_EPAYMENT;
  const isPaymentMethodByPayTo = payment?.paymentMethod === METHOD_PAYTO;

  const isShowFeeDiscount = Boolean(
    sendAmount && feeAmount && originalFee - feeAmount > 0,
  );

  const isShowFee = Boolean(sendAmount && feeAmount);

  const isAgreementByBSB = !paytoAgreement?.payId;

  useEffect(() => {
    return () => {
      dispatch({
        type: SET_TRANSACTION_DETAILS,
        payload: null,
      });
    };
  }, []);

  const fetchOriginalFee = async (countryCode) => {
    throttle(async () => {
      try {
        const { data } = await api.lookupOriginalFee(
          countryCode,
          payout?.dmCode,
          sendAmount,
          receiveAmountAnother ? 'AUD' : currencyCode,
          payout?.pickupOffice?.pickUpOfficeId,
        );

        if (data) {
          setOriginalFee(numberUtils.mathRound(data?.feeAmount));
        }
      } catch (error) {
        checkMaintenance(error);
        console.error(error?.message);
      }
    });
  };

  useEffect(() => {
    if (countryCode) {
      fetchOriginalFee(countryCode);
    }
  }, [countryCode]);

  const discountPercent = SENDING_AMOUNT_DISCOUNT_PERCENTS.find((o) => {
    if (o.range.length === 1 && sendAmount >= o.range[0]) return o;

    if (sendAmount >= o.range[0] && sendAmount <= o.range[1]) return o;

    return null;
  });

  const isShowDiscountPercent = Boolean(
    originalFee && originalFee !== feeAmount && !currencyCodeAnother,
  );

  const instructionTextReference = {
    [STATUS_SUBMITTED]: () => {
      if (isPaymentMethodByDeposit) {
        return stringUtils.replaceBreakLine(
          t('transactions_summary_desc_13', {
            dueDateTime,
          }),
        );
      }

      if (isPaymentMethodByPayId) {
        return stringUtils.replaceBreakLine(
          t('transactions_summary_desc_17', {
            dueDateTime,
          }),
        );
      }

      if (isPaymentMethodByEPayment) {
        return stringUtils.replaceBreakLine(
          t('transactions_summary_desc_18', {
            dueDateTime,
          }),
        );
      }

      if (isPaymentMethodByPayTo && allowMakePayment) {
        return stringUtils.replaceBreakLine(
          t('transactions_summary_desc_22', {
            dueDateTime,
          }),
        );
      }

      if (isPaymentMethodByPayTo && !allowMakePayment) {
        return stringUtils.replaceBreakLine(
          t('transactions_summary_desc_23', {
            dueDateTime,
          }),
        );
      }

      return null;
    },
    [STATUS_PROCESSING]: () => {
      return stringUtils.replaceBreakLine(t('transactions_summary_desc_4'));
    },
    [STATUS_DELIVERING]: () => {
      if (isPayoutMethodByBank) {
        return stringUtils.replaceBreakLine(t('transactions_summary_desc_19'));
      }

      if (isPayoutMethodByHome) {
        return stringUtils.replaceBreakLine(t('transactions_summary_desc_5'));
      }

      if (isPayoutMethodByPickup) {
        return stringUtils.replaceBreakLine(
          t('transactions_summary_desc_6', {
            payoutPin: payout?.payoutPIN,
          }),
        );
      }

      if (isPayoutMethodByEWallet) {
        return stringUtils.replaceBreakLine(t('transactions_summary_desc_20'));
      }

      return null;
    },
    [STATUS_COMPLETED]: () => {
      if (isPayoutMethodByBank) {
        return stringUtils.replaceBreakLine(t('transactions_summary_desc_8'));
      }

      if (isPayoutMethodByPickup) {
        return stringUtils.replaceBreakLine(t('transactions_summary_desc_12'));
      }

      if (isPayoutMethodByHome) {
        return stringUtils.replaceBreakLine(t('transactions_summary_desc_15'));
      }

      if (isPayoutMethodByEWallet) {
        return stringUtils.replaceBreakLine(t('transactions_summary_desc_21'));
      }

      return null;
    },
    [STATUS_CANCELLED]: () => {
      if (!paid) {
        return stringUtils.replaceBreakLine(t('transactions_summary_desc_9'));
      }

      return stringUtils.replaceBreakLine(t('transactions_summary_desc_10'));
    },
    [STATUS_ON_HOLD]: () => {
      return stringUtils.replaceBreakLine(t('transactions_summary_desc_11'));
    },
  };

  const payoutMethodBankRender = () => {
    const getFieldConfigLabel = (config) => config?.name || config?.type;

    let bankRender = `${t('label_name').toUpperCase()}: ${
      payout.bank?.accountName
    } <br/> ${t('label_number').toUpperCase()}: ${
      payout.bank?.accountNumber
        ? formatter.mask(
            getNumberUnmaskStr(payout.bank?.accountNumber, accountNumberFormat),
            accountNumberFormat,
          )
        : ''
    }`;
    if (accountBsbConfig?.visible) {
      bankRender += `<br/> ${(getFieldConfigLabel(accountBsbConfig) || 'BSB').toUpperCase()}: ${
        payout.bank?.branchCode
          ? formatter.mask(
              getNumberUnmaskStr(payout.bank?.branchCode, accountBsbFormat),
              accountBsbFormat,
            )
          : ''
      }`;
    }
    if (accountSwiftConfig?.visible) {
      bankRender += `<br/> ${(getFieldConfigLabel(accountSwiftConfig) || 'SWIFT').toUpperCase()}: ${
        payout.bank?.swiftCode
          ? formatter.mask(
              getNumberUnmaskStr(payout.bank?.swiftCode, accountSwiftFormat),
              accountSwiftFormat,
            )
          : ''
      }`;
    }
    return bankRender;
  };
  const payoutMethodReference = {
    [METHOD_PICKUP]: {
      text: payout.pickupOffice?.fullAddress,
      spanText: '',
    },
    [METHOD_HOME]: {
      text: fullAddress,
      spanText: '',
    },
    [METHOD_BANK]: {
      text: payout.bank?.bankName,
      spanText: payoutMethodBankRender(),
    },
    [METHOD_EWALLET]: {
      text: payout.ewallet?.ewalletProviderName,
      spanText: `ID: ${
        payout.ewallet?.ewalletNumber
          ? formatter.mask(
              getNumberUnmaskStr(
                payout.ewallet?.ewalletNumber,
                ewalletNumberFormat,
              ),
              ewalletNumberFormat,
            )
          : ''
      }`,
    },
  };

  const paymentMethodReference = {
    [STATUS_SUBMITTED]: () => {
      if (
        payment?.paymentMethod === METHOD_POLI ||
        payment?.paymentMethod === METHOD_EPAYMENT
      ) {
        return {
          text: '-',
          spanText: null,
          iconSrc: null,
        };
      }

      if (payment?.paymentMethod === METHOD_DEPOSIT) {
        return {
          text: t(
            METHOD_TRANSLATE_REFERECES[
              PAYMENT_METHOD_REFERENCE[METHOD_DEPOSIT].label
            ],
          ),
          spanText: null,
          iconSrc: null,
        };
      }

      if (payment?.paymentMethod === METHOD_PAYID) {
        return {
          text: t(PAYMENT_METHOD_REFERENCE[METHOD_PAYID].label),
          spanText: null,
          iconSrc: null,
        };
      }

      if (payment?.paymentMethod === METHOD_PAYTO) {
        return {
          text: t(PAYMENT_METHOD_REFERENCE[METHOD_PAYTO].label),
          spanText: isAgreementByBSB
            ? `${paytoAgreement?.bankCode || ''}${
                paytoAgreement?.accountNumber
              }`
            : `PAYID\n(${paytoAgreement?.payId})`,
          iconSrc: null,
        };
      }
    },
    [STATUS_PROCESSING]: () => {
      if (payment?.paymentMethod === METHOD_POLI) {
        return {
          text: null,
          spanText: null,
          iconSrc: PAYMENT_METHOD_REFERENCE[METHOD_POLI].iconSrc,
        };
      }

      if (payment?.paymentMethod === METHOD_DEPOSIT) {
        return {
          text: t(
            METHOD_TRANSLATE_REFERECES[
              PAYMENT_METHOD_REFERENCE[METHOD_DEPOSIT].label
            ],
          ),
          spanText: null,
          iconSrc: null,
        };
      }

      if (payment?.paymentMethod === METHOD_PAYID) {
        return {
          text: t(PAYMENT_METHOD_REFERENCE[METHOD_PAYID].label),
          spanText: null,
          iconSrc: null,
        };
      }

      if (payment?.paymentMethod === METHOD_EPAYMENT) {
        return {
          text: `${t('label_visa_end_with')} ${
            payment.creaditCardNo?.split('*')?.slice(-1)[0]
          }`,
          spanText: null,
          iconSrc: PAYMENT_METHOD_REFERENCE[METHOD_EPAYMENT].iconSrc,
        };
      }

      if (payment?.paymentMethod === METHOD_PAYTO) {
        return {
          text: t(PAYMENT_METHOD_REFERENCE[METHOD_PAYTO].label),
          spanText: isAgreementByBSB
            ? `${paytoAgreement?.bankCode || ''}${
                paytoAgreement?.accountNumber
              }`
            : `PAYID\n(${paytoAgreement?.payId})`,
          iconSrc: null,
        };
      }
    },
    [STATUS_DELIVERING]: () => {
      if (payment?.paymentMethod === METHOD_POLI) {
        return {
          text: null,
          spanText: null,
          iconSrc: PAYMENT_METHOD_REFERENCE[METHOD_POLI].iconSrc,
        };
      }

      if (payment?.paymentMethod === METHOD_DEPOSIT) {
        return {
          text: t(
            METHOD_TRANSLATE_REFERECES[
              PAYMENT_METHOD_REFERENCE[METHOD_DEPOSIT].label
            ],
          ),
          spanText: null,
          iconSrc: null,
        };
      }

      if (payment?.paymentMethod === METHOD_PAYID) {
        return {
          text: t(PAYMENT_METHOD_REFERENCE[METHOD_PAYID].label),
          spanText: null,
          iconSrc: null,
        };
      }

      if (payment?.paymentMethod === METHOD_EPAYMENT) {
        return {
          text: `${t('label_visa_end_with')} ${
            payment.creaditCardNo?.split('*')?.slice(-1)[0]
          }`,
          spanText: null,
          iconSrc: PAYMENT_METHOD_REFERENCE[METHOD_EPAYMENT].iconSrc,
        };
      }

      if (payment?.paymentMethod === METHOD_PAYTO) {
        return {
          text: t(PAYMENT_METHOD_REFERENCE[METHOD_PAYTO].label),
          spanText: isAgreementByBSB
            ? `${paytoAgreement?.bankCode || ''}${
                paytoAgreement?.accountNumber
              }`
            : `PAYID\n(${paytoAgreement?.payId})`,
          iconSrc: null,
        };
      }
    },
    [STATUS_COMPLETED]: () => {
      if (payment?.paymentMethod === METHOD_POLI) {
        return {
          text: null,
          spanText: null,
          iconSrc: PAYMENT_METHOD_REFERENCE[METHOD_POLI].iconSrc,
        };
      }

      if (payment?.paymentMethod === METHOD_DEPOSIT) {
        return {
          text: t(
            METHOD_TRANSLATE_REFERECES[
              PAYMENT_METHOD_REFERENCE[METHOD_DEPOSIT].label
            ],
          ),
          spanText: null,
          iconSrc: null,
        };
      }

      if (payment?.paymentMethod === METHOD_PAYID) {
        return {
          text: t(PAYMENT_METHOD_REFERENCE[METHOD_PAYID].label),
          spanText: null,
          iconSrc: null,
        };
      }

      if (payment?.paymentMethod === METHOD_EPAYMENT) {
        return {
          text: `${t('label_visa_end_with')} ${
            payment.creaditCardNo?.split('*')?.slice(-1)[0]
          }`,
          spanText: null,
          iconSrc: PAYMENT_METHOD_REFERENCE[METHOD_EPAYMENT].iconSrc,
        };
      }

      if (payment?.paymentMethod === METHOD_PAYTO) {
        return {
          text: t(PAYMENT_METHOD_REFERENCE[METHOD_PAYTO].label),
          spanText: isAgreementByBSB
            ? `${paytoAgreement?.bankCode || ''}${
                paytoAgreement?.accountNumber
              }`
            : `PAYID\n(${paytoAgreement?.payId})`,
          iconSrc: null,
        };
      }
    },
    [STATUS_CANCELLED]: () => {
      if (paid) {
        if (payment?.paymentMethod === METHOD_POLI) {
          return {
            text: null,
            spanText: null,
            iconSrc: PAYMENT_METHOD_REFERENCE[METHOD_POLI].iconSrc,
          };
        }

        if (payment?.paymentMethod === METHOD_DEPOSIT) {
          return {
            text: t(
              METHOD_TRANSLATE_REFERECES[
                PAYMENT_METHOD_REFERENCE[METHOD_DEPOSIT].label
              ],
            ),
            spanText: null,
            iconSrc: null,
          };
        }

        if (payment?.paymentMethod === METHOD_PAYID) {
          return {
            text: t(PAYMENT_METHOD_REFERENCE[METHOD_PAYID].label),
            spanText: null,
            iconSrc: null,
          };
        }

        if (payment?.paymentMethod === METHOD_EPAYMENT) {
          return {
            text: `${t('label_visa_end_with')} ${
              payment.creaditCardNo?.split('*')?.slice(-1)[0]
            }`,
            spanText: null,
            iconSrc: PAYMENT_METHOD_REFERENCE[METHOD_EPAYMENT].iconSrc,
          };
        }

        if (payment?.paymentMethod === METHOD_PAYTO) {
          return {
            text: t(PAYMENT_METHOD_REFERENCE[METHOD_PAYTO].label),
            spanText: isAgreementByBSB
              ? `${paytoAgreement?.bankCode || ''}${
                  paytoAgreement?.accountNumber
                }`
              : `PAYID\n(${paytoAgreement?.payId})`,
            iconSrc: null,
          };
        }
      }

      return {
        text: '-',
        spanText: null,
        iconSrc: null,
      };
    },
    [STATUS_ON_HOLD]: () => {
      if (paid) {
        if (payment?.paymentMethod === METHOD_POLI) {
          return {
            text: null,
            spanText: null,
            iconSrc: PAYMENT_METHOD_REFERENCE[METHOD_POLI].iconSrc,
          };
        }

        if (payment?.paymentMethod === METHOD_DEPOSIT) {
          return {
            text: t(
              METHOD_TRANSLATE_REFERECES[
                PAYMENT_METHOD_REFERENCE[METHOD_DEPOSIT].label
              ],
            ),
            spanText: null,
            iconSrc: null,
          };
        }

        if (payment?.paymentMethod === METHOD_PAYID) {
          return {
            text: t(PAYMENT_METHOD_REFERENCE[METHOD_PAYID].label),
            spanText: null,
            iconSrc: null,
          };
        }

        if (payment?.paymentMethod === METHOD_EPAYMENT) {
          return {
            text: `${t('label_visa_end_with')} ${
              payment.creaditCardNo?.split('*')?.slice(-1)[0]
            }`,
            spanText: null,
            iconSrc: PAYMENT_METHOD_REFERENCE[METHOD_EPAYMENT].iconSrc,
          };
        }

        if (payment?.paymentMethod === METHOD_PAYTO) {
          return {
            text: t(PAYMENT_METHOD_REFERENCE[METHOD_PAYTO].label),
            spanText: isAgreementByBSB
              ? `${paytoAgreement?.bankCode || ''}${
                  paytoAgreement?.accountNumber
                }`
              : `PAYID\n(${paytoAgreement?.payId})`,
            iconSrc: null,
          };
        }
      }

      return {
        text: '-',
        spanText: null,
        iconSrc: null,
      };
    },
  };

  const isShowSurchargeField =
    payment?.paymentMethod === METHOD_EPAYMENT &&
    Number(surchargeAmount) > 0;

  const totalLabelReference = {
    [STATUS_SUBMITTED]: () => {
      return t('label_total_to_pay');
    },
    [STATUS_PROCESSING]: () => {
      return t('label_total_paid');
    },
    [STATUS_DELIVERING]: () => {
      return t('label_total_paid');
    },
    [STATUS_COMPLETED]: () => {
      return t('label_total_paid');
    },
    [STATUS_CANCELLED]: () => {
      if (paid) {
        return t('label_total_paid');
      }

      return t('label_total_to_pay');
    },
    [STATUS_ON_HOLD]: () => {
      return t('label_total_paid');
    },
  };

  const isShowIntroductionIcon = [METHOD_DEPOSIT, METHOD_PAYID].includes(
    payment?.paymentMethod,
  );

  const introductionIconReference = {
    [STATUS_SUBMITTED]: () => {
      if (isShowIntroductionIcon) {
        return true;
      }

      return false;
    },
    [STATUS_PROCESSING]: () => {
      if (isShowIntroductionIcon) {
        return true;
      }

      return false;
    },
    [STATUS_DELIVERING]: () => {
      if (isShowIntroductionIcon) {
        return true;
      }

      return false;
    },
    [STATUS_COMPLETED]: () => {
      if (isShowIntroductionIcon) {
        return true;
      }

      return false;
    },
    [STATUS_CANCELLED]: () => {
      if (isShowIntroductionIcon && paid) {
        return true;
      }

      return false;
    },
    [STATUS_ON_HOLD]: () => {
      if (isShowIntroductionIcon && paid) {
        return true;
      }

      return false;
    },
  };

  const renderActionsButton = () => {
    if (
      payment?.paymentMethod === METHOD_PAYTO &&
      allowMakePayment &&
      status === STATUS_SUBMITTED
    ) {
      return (
        <>
          <ButtonCommon
            value={t('button_close')}
            onClick={onClose}
            styles={{
              margin: '0px',
              width: '50%',
            }}
            color="var(--ds-c-blue)"
            background="var(--ds-c-white)"
          />
          <ButtonCommon
            value={t('button_other_payments')}
            onClick={onClick}
            styles={{
              margin: '0px',
              width: '50%',
            }}
            color="var(--c-primary)"
            background="var(--bg-primary)"
            isFill={true}
          />
        </>
      );
    }

    if (
      payment?.paymentMethod !== METHOD_PAYTO &&
      status === STATUS_SUBMITTED
    ) {
      return (
        <>
          <ButtonCommon
            value={t('button_close')}
            onClick={onClose}
            styles={{
              margin: '0px',
              width: '50%',
            }}
            color="var(--ds-c-blue)"
            background="var(--ds-c-white)"
          />
          <ButtonCommon
            value={t('button_other_payments')}
            onClick={onClick}
            styles={{
              margin: '0px',
              width: '50%',
            }}
            color="var(--c-primary)"
            background="var(--bg-primary)"
            isFill={true}
          />
        </>
      );
    }

    return (
      <ButtonCommon
        value={t('button_close')}
        onClick={onClose}
        styles={{
          margin: '0px',
          width: '50%',
          marginInline: 'auto',
        }}
        color="var(--c-primary)"
        background="var(--bg-primary)"
      />
    );
  };

  const renderPaymentValue = () => (
    <>
      {paymentMethodReference[status]?.()?.iconSrc ? (
        <>
          <img src={paymentMethodReference[status]?.()?.iconSrc} alt="" />
          {introductionIconReference[status]?.() && (
            <img
              src={isHHMTTheme ? IntroductionHHMTIcon : IntroductionRemoxIcon}
              alt=""
              onClick={handleOnToggleInstructionPopup}
              style={{
                marginLeft: '8px',
                cursor: 'pointer',
                paddingBottom: '0px',
              }}
            />
          )}
          {paymentMethodReference[status]?.()?.text && (
            <span className="credit-card">
              {paymentMethodReference[status]?.()?.text}
            </span>
          )}
        </>
      ) : (
        <>
          {paymentMethodReference[status]?.()?.text}
          {introductionIconReference[status]?.() && (
            <img
              src={isHHMTTheme ? IntroductionHHMTIcon : IntroductionRemoxIcon}
              alt=""
              onClick={handleOnToggleInstructionPopup}
              style={{
                marginLeft: '8px',
                cursor: 'pointer',
                paddingBottom: '4px',
              }}
            />
          )}
          {paymentMethodReference[status]?.()?.spanText && (
            <>
              <br />
              <span>{paymentMethodReference[status]?.().spanText}</span>
            </>
          )}
        </>
      )}
    </>
  );

  const handleOnToggleInstructionPopup = () => {
    setOpenInstructionPopup(!isOpenInstructionPopup);
  };

  const renderContactNumber = () => {
    if (mobileConfig?.visible && phoneConfig?.visible) {
      if (mobileNumberMask && phoneNumberMask) {
        return `${mobileNumberMask || '-'} / ${phoneNumberMask || '-'}`;
      }

      if (mobileNumberMask && !phoneNumberMask) {
        return `${mobileNumberMask || '-'}`;
      }

      if (!mobileNumberMask && phoneNumberMask) {
        return `${phoneNumberMask || '-'}`;
      }

      return `${mobileNumberMask || '-'}`;
    }

    if (mobileConfig?.visible && !phoneConfig?.visible) {
      return `${mobileNumberMask || '-'}`;
    }

    if (!mobileConfig?.visible && phoneConfig?.visible) {
      return `${phoneNumberMask || '-'}`;
    }

    return '-';
  };

  const labelPayoutMethodReference = {
    [METHOD_HOME]: t('label_delivery_to'),
    [METHOD_PICKUP]: t('label_pickup_at'),
    [METHOD_BANK]: t('label_credit_to'),
    [METHOD_EWALLET]: t('label_credit_to'),
  };

  useAutoShowEventModal({
    screenId: SCREEN_ID,
    params: [],
    modalStyles: {},
    modalOptions: {},
  });

  return (
    <>
      {isOpenInstructionPopup && (
        <InstructionPopup
          isOpen={isOpenInstructionPopup}
          onClose={handleOnToggleInstructionPopup}
          method={payment?.paymentMethod}
        />
      )}

      <ModalStyled
        show={isOpen}
        onHide={() => {}}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <ModalHeader>
          <RefStatusWrap>
            <RefText>
              <span>REF NO.</span> {`${refNumber}`}
            </RefText>
            <TransactionStatus transaction={information} />
          </RefStatusWrap>
          <IntructionText
            dangerouslySetInnerHTML={{
              __html: instructionTextReference[status]?.(),
            }}
          />
        </ModalHeader>
        <ModalBody>
          <FieldWrap
            styles={{
              alignItems: 'start',
              padding: '10px 0',
            }}
          >
            <FieldLabel>{t('label_receiver')}</FieldLabel>
            <FieldValue>{fullName}</FieldValue>
          </FieldWrap>
          {(mobileConfig?.visible || phoneConfig?.visible) && (
            <FieldWrap>
              <FieldLabel>{t('label_contact_number')}</FieldLabel>
              <FieldValue>{renderContactNumber()}</FieldValue>
            </FieldWrap>
          )}
          <FieldWrap
            styles={{
              alignItems: 'start',
              padding: '10px 0',
            }}
            style={{
              marginInline: '-16px',
              paddingInline: '16px',
              backgroundColor: '#FBFBFB',
            }}
          >
            <FieldLabel>
              {labelPayoutMethodReference[payout?.dmCode]}
            </FieldLabel>
            <FieldValue>
              {payoutMethodReference[payout?.dmCode]?.text}
              {Boolean(payoutMethodReference[payout?.dmCode]?.spanText) && (
                <>
                  <br />
                  <span
                    dangerouslySetInnerHTML={{
                      __html: payoutMethodReference[payout?.dmCode].spanText,
                    }}
                  />
                </>
              )}
            </FieldValue>
          </FieldWrap>
          <FieldWrap
            styles={{
              alignItems: 'start',
              padding: '10px 0',
            }}
          >
            <FieldLabel>{t('label_receiving_amount')}</FieldLabel>
            <FieldValue>
              {receiveAmount &&
                `${format.toAmountCentStr(receiveAmount)} ${currencyCode}`}
              <br />
              <span className="rate">{`(1 AUD = ${format.toAmountCentStr(
                rateValue,
              )} ${currencyCode})`}</span>
              {Boolean(currencyCodeAnother) && (
                <>
                  <br />
                  <br />
                  {`${format.toAmountCentStr(
                    receiveAmountAnother,
                  )} ${currencyCodeAnother}`}
                  <br />
                  <span className="rate">{`(1 AUD = ${format.toAmountCentStr(
                    rateValueAnother,
                  )} ${currencyCodeAnother})`}</span>
                </>
              )}
            </FieldValue>
          </FieldWrap>
          <FieldWrap
            styles={{
              alignItems: 'start',
              padding: '10px 0',
            }}
          >
            <FieldLabel
              styles={{
                padding: '3px 0 0 0',
              }}
            >
              {t('label_payment_method')}
            </FieldLabel>
            <FieldValue>{renderPaymentValue()}</FieldValue>
          </FieldWrap>
          <FieldWrap>
            <FieldLabel>{t('label_sending_date')}</FieldLabel>
            <FieldValue>{sendingDate}</FieldValue>
          </FieldWrap>
          <CustomCalculatorFormAccordion
            status={status}
            totalLabelReference={totalLabelReference}
            totalPayAmount={totalPayAmount}
            sendAmount={sendAmount}
            feeAmount={feeAmount}
            isShowDiscountPercent={isShowDiscountPercent}
            discountPercent={discountPercent}
            originalFee={originalFee}
            isShowSurchargeField={isShowSurchargeField}
            surchargeAmount={surchargeAmount}
            isDiscountApplied={isDiscountApplied}
            discountAmount={discountAmount}
            discountApplied={discountApplied}
            isShowFeeDiscount={isShowFeeDiscount}
            isShowFee={isShowFee}
          />
        </ModalBody>
        <ModalFooter>{renderActionsButton()}</ModalFooter>
      </ModalStyled>
    </>
  );
};

const ModalStyled = styled(Modal)`
  & .modal-dialog {
    transform: none;
  }
  & .modal-content {
    padding: 16px;
    border-radius: 12px;
    box-shadow: var(--ds-bs-4);
    background: var(--ds-c-white);
    width: 470px;
    height: fit-content;
    margin-inline: auto;
  }
`;
const ModalHeader = styled(Modal.Header)`
  padding: 0;
  margin-bottom: 0;
  display: block;
  padding-bottom: 16px;
  border-bottom: none;
`;
const RefStatusWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;
const RefText = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  color: var(--ds-c-blue-hyperlink-default);
  text-align: left;

  margin: 0;
  padding: 0;

  & span {
    font-family: var(--ff-primary);
    font-weight: 600;
    color: var(--ds-c-black);
  }
`;
const IntructionText = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-black);
  white-space: break-spaces;

  margin: 0;
  padding: 0;

  & span {
    color: var(--ds-c-green-default);
  }
`;

const ModalBody = styled(Modal.Body)`
  padding: 0;
  margin-bottom: 0px;
`;
const FieldWrap = styled.div`
  display: flex;
  align-items: ${(props) => props.styles?.alignItems || 'center'};
  justify-content: start;
  min-height: 44px;
  height: fit-content;
  padding: ${(props) => props.styles?.padding};
`;
const FieldLabel = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-blue);
  ${textBreakLineAllMixin};

  margin: 0;
  padding: ${(props) => props.styles?.padding || '0'};
  min-width: 150px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;
const FieldValue = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;
  text-align: right;
  color: var(--ds-c-black);
  ${textBreakLineAllMixin};

  width: 100%;
  margin: 0;
  padding: 0;
  padding-left: 8px;

  & span {
    color: var(--ds-c-grey-neutral);
    ${textBreakLineAllMixin};
  }

  & span.rate {
    color: var(--ds-c-blue);
  }

  & span.credit-card {
    text-transform: initial;
    color: var(--ds-c-grey-dark);
    margin-left: 4px;
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const ModalFooter = styled(Modal.Footer)`
  border: none;
  padding: 0;
  display: flex;
  justify-content: space-between;
`;

export default FormTransactionDetails;
